import { ComponentProps } from 'preact';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { Chip } from '../UNSAFE_Chip';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '../resources/nls/bundle';

const rootStyles = cssProps`${{
  display: 'inline-flex'
}}`;

type PickedPropsFromChip = Pick<
  ComponentProps<typeof Chip>,
  'accessibleLabel' | 'isDisabled' | 'isSelected' | 'onToggle'
>;

type Props = PickedPropsFromChip & {
  count?: number;
  onKeyDown?: (event: KeyboardEvent) => void;
  onKeyUp?: (event: KeyboardEvent) => void;
  onMouseDown?: (event: MouseEvent) => void;
};

// This component renders a badge showing the number of selected values
export function SelectedValuesCount({
  accessibleLabel,
  count = 0,
  onKeyDown,
  onKeyUp,
  onMouseDown,
  ...passThroughProps
}: Props) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const instructions = translations.selectMultiple_showSelectedValues();
  const accLabel = accessibleLabel ? `${accessibleLabel}. ` : '';

  const ninetyNinePlus = translations.selectMultiple_countPlus({ COUNT: `99` });
  // The Chip shows the number of values selected or 99+ if the number is over 99.
  const chipCount = count > 99 ? `${ninetyNinePlus}` : `${count}`;
  // The screenreader will hear what the number of selected values is along with instructions
  // like Click to see the full list.
  const valuesSelectedText = translations.selectMultiple_valuesSelected({
    VALUE_COUNT: `${count}`
  });
  const label = `${accLabel}${valuesSelectedText} ${instructions}`;
  return (
    <div class={rootStyles} onKeyDown={onKeyDown} onKeyUp={onKeyUp} onMouseDown={onMouseDown}>
      <Chip accessibleLabel={label} {...passThroughProps}>
        {chipCount}
      </Chip>
    </div>
  );
}
